import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import $api from 'http/index'
import Notification from 'utils/Notifications'

import { Spinner, ErrorState, EmptyState } from 'components'
import SyllabusHeader from './components/SyllabusHeader/SyllabusHeader'
import SemesterSection from './components/SemesterSection/SemesterSection'
import AddSubjectDialog from './components/AddSubjectDialog/AddSubjectDialog'
import AddThemeDialog from './components/AddThemeDialog/AddThemeDialog'
import styles from './SyllabusBuilderPage.module.scss'

const SyllabusBuilderPage = () => {
  const { t } = useTranslation('common')
  const { id } = useParams()
  const [syllabus, setSyllabus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [addSubjectOpen, setAddSubjectOpen] = useState(null)
  const [subjectName, setSubjectName] = useState('')
  const [addThemeOpen, setAddThemeOpen] = useState(null)
  const [themeForm, setThemeForm] = useState({ title: '', hours: 2 })
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const STATUS_LABELS = useMemo(
    () => ({
      draft: { label: t('syllabus.draft'), cls: 'unv-badge--muted' },
      pending: { label: t('syllabus.pending'), cls: 'unv-badge--warning' },
      approved: { label: t('syllabus.approved'), cls: 'unv-badge--success' },
      rejected: { label: t('syllabus.rejected'), cls: 'unv-badge--danger' },
    }),
    [t]
  )

  const load = async () => {
    if (!id) {
      setLoading(false)
      setSyllabus({ id: null, title: t('syllabus.default_title'), status: 'draft', semesters: [] })
      return
    }
    setLoading(true)
    setError(false)
    try {
      const res = await $api.get(`/syllabus/${id}/`)
      setSyllabus(res.data)
    } catch {
      setError(true)
      setSyllabus(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const moveItem = (arr, idx, dir) => {
    const next = [...arr]
    const target = idx + dir
    if (target < 0 || target >= next.length) return next
    ;[next[idx], next[target]] = [next[target], next[idx]]
    return next
  }

  const updateSubjects = (semesterIdx, fn) => {
    setSyllabus((s) => {
      const semesters = [...(s.semesters || [])]
      const sem = { ...semesters[semesterIdx] }
      sem.subjects = fn(sem.subjects || [])
      semesters[semesterIdx] = sem
      return { ...s, semesters }
    })
  }

  const updateThemes = (semesterIdx, subjectIdx, fn) => {
    setSyllabus((s) => {
      const semesters = [...(s.semesters || [])]
      const sem = { ...semesters[semesterIdx] }
      const subjects = [...(sem.subjects || [])]
      const subj = { ...subjects[subjectIdx] }
      subj.themes = fn(subj.themes || [])
      subjects[subjectIdx] = subj
      sem.subjects = subjects
      semesters[semesterIdx] = sem
      return { ...s, semesters }
    })
  }

  const addSubject = () => {
    if (addSubjectOpen == null || !subjectName.trim()) return
    updateSubjects(addSubjectOpen, (subjects) => [
      ...subjects,
      { id: `tmp-${Date.now()}`, name: subjectName.trim(), themes: [] },
    ])
    setSubjectName('')
    setAddSubjectOpen(null)
  }

  const addTheme = () => {
    if (!addThemeOpen || !themeForm.title.trim()) return
    updateThemes(addThemeOpen.semesterIdx, addThemeOpen.subjectIdx, (themes) => [
      ...themes,
      { id: `tmp-${Date.now()}`, title: themeForm.title.trim(), hours: Number(themeForm.hours) || 0 },
    ])
    setThemeForm({ title: '', hours: 2 })
    setAddThemeOpen(null)
  }

  const removeSubject = (semIdx, subjIdx) => {
    updateSubjects(semIdx, (subjects) => subjects.filter((_, i) => i !== subjIdx))
  }

  const removeTheme = (semIdx, subjIdx, themeIdx) => {
    updateThemes(semIdx, subjIdx, (themes) => themes.filter((_, i) => i !== themeIdx))
  }

  const save = async () => {
    if (!syllabus) return
    setSaving(true)
    try {
      if (syllabus.id) {
        await $api.patch(`/syllabus/${syllabus.id}/`, syllabus)
      } else {
        const res = await $api.post('/syllabus/', syllabus)
        setSyllabus(res.data)
      }
      setNotify({ isOpen: true, message: t('syllabus.saved_msg'), type: 'success', sound: 'success' })
    } catch {
      setNotify({ isOpen: true, message: t('syllabus.save_error'), type: 'error', sound: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const sendForApproval = async () => {
    if (!syllabus?.id) {
      setNotify({ isOpen: true, message: t('syllabus.save_first'), type: 'warning', sound: 'warning' })
      return
    }
    try {
      await $api.post(`/syllabus/${syllabus.id}/submit/`)
      setNotify({ isOpen: true, message: t('syllabus.sent_to_dean'), type: 'success', sound: 'success' })
      load()
    } catch {
      setNotify({ isOpen: true, message: t('syllabus.error'), type: 'error', sound: 'error' })
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loaderWrap}>
          <Spinner.Center />
        </div>
      </div>
    )
  }

  if (error || !syllabus) {
    return (
      <div className={styles.page}>
        <ErrorState onRetry={load} description={t('syllabus.section_in_dev')} />
      </div>
    )
  }

  const status = STATUS_LABELS[syllabus.status] || { label: syllabus.status || '—', cls: '' }

  return (
    <div className={styles.page}>
      <SyllabusHeader
        syllabus={syllabus}
        status={status}
        saving={saving}
        onSave={save}
        onSubmit={sendForApproval}
      />

      {(syllabus.semesters || []).length === 0 && (
        <EmptyState iconName="syllabus" title={t('syllabus.no_semesters_hint')} />
      )}

      {(syllabus.semesters || []).map((sem, semIdx) => (
        <SemesterSection
          key={sem.id || semIdx}
          sem={sem}
          semIdx={semIdx}
          moveItem={moveItem}
          updateSubjects={updateSubjects}
          updateThemes={updateThemes}
          setAddSubjectOpen={setAddSubjectOpen}
          setAddThemeOpen={setAddThemeOpen}
          removeSubject={removeSubject}
          removeTheme={removeTheme}
        />
      ))}

      <AddSubjectDialog
        open={addSubjectOpen}
        onClose={() => setAddSubjectOpen(null)}
        subjectName={subjectName}
        setSubjectName={setSubjectName}
        onAdd={addSubject}
      />

      <AddThemeDialog
        open={addThemeOpen}
        onClose={() => setAddThemeOpen(null)}
        themeForm={themeForm}
        setThemeForm={setThemeForm}
        onAdd={addTheme}
      />

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default SyllabusBuilderPage
